/**
 * App State Service
 * 
 * Manages app lifecycle events and triggers appropriate sync operations
 * based on app state changes (active, background, inactive).
 */

import { App, AppState } from '@capacitor/app';
import { isNativeApp } from '@/utils/platformUtils';

/**
 * App state change events
 */
export type AppStateEvent = 
  | { type: 'app_became_active'; data: { previousState: string; timestamp: string } }
  | { type: 'app_went_background'; data: { activeTime: number; timestamp: string } }
  | { type: 'app_became_inactive'; data: { reason: string; timestamp: string } }
  | { type: 'app_resumed_from_background'; data: { backgroundTime: number; timestamp: string } }
  | { type: 'app_will_terminate'; data: { timestamp: string } };

export type AppStateEventListener = (event: AppStateEvent) => void;

/**
 * App state configuration
 */
export interface AppStateConfig {
  // Sync triggers
  syncOnAppResume: boolean;
  syncOnAppBackground: boolean;
  syncOnAppBecomeActive: boolean;
  
  // Timing settings
  backgroundSyncDelay: number; // milliseconds to wait before background sync
  resumeSyncDelay: number; // milliseconds to wait before resume sync
  minBackgroundTime: number; // minimum time in background before triggering resume sync
  
  // State tracking
  trackActiveTime: boolean;
  trackBackgroundTime: boolean;
  
  // Debug settings
  enableDebugLogging: boolean;
}

/**
 * App state statistics
 */
export interface AppStateStats {
  totalActiveTime: number;
  totalBackgroundTime: number;
  activeSessions: number;
  backgroundSessions: number;
  lastActiveTime: string | null;
  lastBackgroundTime: string | null;
  currentState: 'active' | 'background' | 'inactive';
}

/**
 * App State Service
 */
export class AppStateService {
  private config: AppStateConfig;
  private eventListeners: Set<AppStateEventListener> = new Set();
  private isInitialized = false;
  
  // State tracking
  private currentState: AppState | null = null;
  private activeStartTime: number | null = null;
  private backgroundStartTime: number | null = null;
  
  // Statistics
  private stats: AppStateStats = {
    totalActiveTime: 0,
    totalBackgroundTime: 0,
    activeSessions: 0,
    backgroundSessions: 0,
    lastActiveTime: null,
    lastBackgroundTime: null,
    currentState: 'inactive'
  };

  // Timers
  private backgroundSyncTimer: NodeJS.Timeout | null = null;
  private resumeSyncTimer: NodeJS.Timeout | null = null;

  constructor(config: Partial<AppStateConfig> = {}) {
    this.config = {
      syncOnAppResume: true,
      syncOnAppBackground: true,
      syncOnAppBecomeActive: true,
      backgroundSyncDelay: 2000, // 2 seconds
      resumeSyncDelay: 1000, // 1 second
      minBackgroundTime: 5000, // 5 seconds
      trackActiveTime: true,
      trackBackgroundTime: true,
      enableDebugLogging: true,
      ...config
    };
  }

  /**
   * Initialize the app state service
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    // Only initialize on native platforms
    if (!isNativeApp()) {
      if (this.config.enableDebugLogging) {
        // Web platform detected, skipping initialization
      }
      return;
    }

    try {
      if (this.config.enableDebugLogging) {
        // Initializing App State Service
      }

      // Get initial app state
      this.currentState = await App.getState();
      
      if (this.currentState.isActive) {
        this.handleAppBecameActive();
      }

      // Set up app state listeners
      App.addListener('appStateChange', (state) => {
        this.handleAppStateChange(state);
      });

      // Set up app URL open listener (for deep links)
      App.addListener('appUrlOpen', (_event) => {
        if (this.config.enableDebugLogging) {
          // App opened via URL
        }
        
        // App was opened via deep link, trigger sync
        if (this.config.syncOnAppBecomeActive) {
          this.triggerSync('app_url_open');
        }
      });

      // Set up app restored listener (iOS)
      App.addListener('appRestoredResult', (_event) => {
        if (this.config.enableDebugLogging) {
          // App restored
        }
        
        // App was restored, trigger sync
        if (this.config.syncOnAppResume) {
          this.triggerSync('app_restored');
        }
      });

      this.isInitialized = true;
      
      if (this.config.enableDebugLogging) {
        // App State Service initialized successfully
      }
    } catch (error) {
      console.error('❌ Failed to initialize App State Service:', error);
      throw error;
    }
  }

  /**
   * Add event listener
   */
  addEventListener(listener: AppStateEventListener): void {
    this.eventListeners.add(listener);
  }

  /**
   * Remove event listener
   */
  removeEventListener(listener: AppStateEventListener): void {
    this.eventListeners.delete(listener);
  }

  /**
   * Emit event to all listeners
   */
  private emitEvent(event: AppStateEvent): void {
    this.eventListeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        console.error('Error in app state event listener:', error);
      }
    });
  }

  /**
   * Handle app state change
   */
  private handleAppStateChange(newState: AppState): void {
    const previousState = this.currentState;
    const timestamp = new Date().toISOString();
    
    if (this.config.enableDebugLogging) {
      console.log('📱 App state changed:', {
        from: previousState?.isActive ? 'active' : 'background',
        to: newState.isActive ? 'active' : 'background',
        timestamp
      });
    }

    // Handle state transitions
    if (previousState && !previousState.isActive && newState.isActive) {
      // App resumed from background
      this.handleAppResumedFromBackground(timestamp);
    } else if (previousState && previousState.isActive && !newState.isActive) {
      // App went to background
      this.handleAppWentBackground(timestamp);
    } else if (!previousState && newState.isActive) {
      // App became active (first time)
      this.handleAppBecameActive();
    }

    // Update current state
    this.currentState = newState;
    this.stats.currentState = newState.isActive ? 'active' : 'background';
  }

  /**
   * Handle app became active
   */
  private handleAppBecameActive(): void {
    const timestamp = new Date().toISOString();
    
    if (this.config.trackActiveTime) {
      this.activeStartTime = Date.now();
      this.stats.activeSessions++;
      this.stats.lastActiveTime = timestamp;
    }

    this.emitEvent({
      type: 'app_became_active',
      data: {
        previousState: 'background',
        timestamp
      }
    });

    if (this.config.syncOnAppBecomeActive) {
      this.scheduleSync('app_became_active', this.config.resumeSyncDelay);
    }
  }

  /**
   * Handle app resumed from background
   */
  private handleAppResumedFromBackground(timestamp: string): void {
    let backgroundTime = 0;
    
    if (this.config.trackBackgroundTime && this.backgroundStartTime) {
      backgroundTime = Date.now() - this.backgroundStartTime;
      this.stats.totalBackgroundTime += backgroundTime;
    }

    if (this.config.trackActiveTime) {
      this.activeStartTime = Date.now();
      this.stats.activeSessions++;
      this.stats.lastActiveTime = timestamp;
    }

    this.emitEvent({
      type: 'app_resumed_from_background',
      data: {
        backgroundTime,
        timestamp
      }
    });

    // Only trigger sync if app was in background for minimum time
    if (this.config.syncOnAppResume && backgroundTime >= this.config.minBackgroundTime) {
      this.scheduleSync('app_resumed', this.config.resumeSyncDelay);
    }
  }

  /**
   * Handle app went to background
   */
  private handleAppWentBackground(timestamp: string): void {
    let activeTime = 0;
    
    if (this.config.trackActiveTime && this.activeStartTime) {
      activeTime = Date.now() - this.activeStartTime;
      this.stats.totalActiveTime += activeTime;
    }

    if (this.config.trackBackgroundTime) {
      this.backgroundStartTime = Date.now();
      this.stats.backgroundSessions++;
      this.stats.lastBackgroundTime = timestamp;
    }

    this.emitEvent({
      type: 'app_went_background',
      data: {
        activeTime,
        timestamp
      }
    });

    if (this.config.syncOnAppBackground) {
      this.scheduleSync('app_background', this.config.backgroundSyncDelay);
    }
  }

  /**
   * Schedule sync operation
   */
  private scheduleSync(trigger: string, delay: number): void {
    // Clear existing timers
    if (this.backgroundSyncTimer) {
      clearTimeout(this.backgroundSyncTimer);
    }
    if (this.resumeSyncTimer) {
      clearTimeout(this.resumeSyncTimer);
    }

    const timer = setTimeout(() => {
      this.triggerSync(trigger);
    }, delay);

    if (trigger.includes('background')) {
      this.backgroundSyncTimer = timer;
    } else {
      this.resumeSyncTimer = timer;
    }
  }

  /**
   * Trigger sync operation
   */
  private async triggerSync(trigger: string): Promise<void> {
    try {
      if (this.config.enableDebugLogging) {
        // Triggering sync
      }

      // Import and trigger the main sync service
      const { syncService } = await import('@/services/offline');
      await syncService.performSync();
    } catch (error) {
      console.error(`❌ Failed to trigger sync (${trigger}):`, error);
    }
  }

  /**
   * Get current app state
   */
  getCurrentState(): AppState | null {
    return this.currentState;
  }

  /**
   * Get app state statistics
   */
  getStats(): AppStateStats {
    // Update current session time if active
    if (this.stats.currentState === 'active' && this.activeStartTime) {
      const currentActiveTime = Date.now() - this.activeStartTime;
      return {
        ...this.stats,
        totalActiveTime: this.stats.totalActiveTime + currentActiveTime
      };
    }

    return { ...this.stats };
  }

  /**
   * Reset statistics
   */
  resetStats(): void {
    this.stats = {
      totalActiveTime: 0,
      totalBackgroundTime: 0,
      activeSessions: 0,
      backgroundSessions: 0,
      lastActiveTime: null,
      lastBackgroundTime: null,
      currentState: this.currentState?.isActive ? 'active' : 'background'
    };
  }

  /**
   * Update configuration
   */
  updateConfig(updates: Partial<AppStateConfig>): void {
    this.config = { ...this.config, ...updates };
  }

  /**
   * Get current configuration
   */
  getConfig(): AppStateConfig {
    return { ...this.config };
  }

  /**
   * Cleanup resources
   */
  cleanup(): void {
    if (this.backgroundSyncTimer) {
      clearTimeout(this.backgroundSyncTimer);
      this.backgroundSyncTimer = null;
    }

    if (this.resumeSyncTimer) {
      clearTimeout(this.resumeSyncTimer);
      this.resumeSyncTimer = null;
    }

    // Remove all listeners
    App.removeAllListeners();
    this.eventListeners.clear();
    
    this.isInitialized = false;

    if (this.config.enableDebugLogging) {
      // App State Service cleaned up
    }
  }
}

// Export singleton instance
export const appStateService = new AppStateService();
